import React, { useState } from 'react';
import DatePicker from '@ajat/design-system/DatePicker';
import { ComponentPage, DemoCard } from '../shared';

export const meta = {
  component: DatePicker,
  title: 'DatePicker',
  group: 'Forms',
  importPath: '@ajat/design-system/DatePicker',
  figmaNode: '3637:10825',
  description:
    'Calendar field built on Input. On desktop it opens an absolutely positioned popup; below 1024px it opens a bottom sheet instead, where tapping a day applies it immediately. The trigger is readOnly, so the mobile keyboard never appears.',
  props: [
    ['label', 'string', "'Date'", 'Floating label'],
    ['value', 'Date', '—', 'Controlled value'],
    ['defaultValue', 'Date', '—', 'Initial value (uncontrolled)'],
    ['onChange', 'func', '—', 'Fires with the selected Date, or null when cleared'],
    ['format', 'string', "'MM/DD/YYYY'", 'Display format of the trigger value'],
    ['minDate', 'Date', '—', 'Earliest selectable date'],
    ['maxDate', 'Date', '—', 'Latest selectable date'],
    ['disabledDates', 'array', '[]', 'Individual dates to block'],
    ['icon', 'node', 'null', 'Leading icon (default Calendar)'],
    ['showIcon', 'bool', 'true', 'Show the leading icon'],
    ['error', 'string', "''", 'Error message — switches the field to its error state'],
    ['helperText', 'string', "''", 'Helper line under the field'],
    ['disabled', 'bool', 'false', 'Disables the field'],
    ['mobileMode', "'auto' | 'sheet' | 'popup'", "'auto'", "'auto' uses a sheet below 1024px and a popup above"],
    ['mobileTitle', 'string', 'the label', 'Heading of the mobile bottom sheet'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['id', 'string', '—', 'Native id'],
    ['name', 'string', '—', 'Native name'],
    ['placeholder', 'string', '—', 'Native placeholder'],
    ['required', 'bool', 'false', 'Native required'],
  ],
  usage: [
    {
      title: 'Basic',
      code: `import DatePicker from '@ajat/design-system/DatePicker';

const [date, setDate] = useState(null);

<DatePicker
  label="Afrejsedato"
  value={date}
  onChange={setDate}
  minDate={new Date()}
/>`,
    },
    {
      title: 'Inside a SidePanel',
      code: `// Never force mobileMode="popup" inside a SidePanel: the popup
// sits at z-index 100 and the panel at 1000, so it would be
// trapped behind it. The default "auto" is correct.
<SidePanel isOpen={open} onClose={close} title="Klasserepræsentant">
  <DatePicker label="Betalingsdato" onChange={setPayment} />
</SidePanel>`,
    },
  ],
};

export default function DatePickerPage() {
  const [date, setDate] = useState(null);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[var(--space-ui-medium-m)]">
          <DatePicker label="Select date" value={date} onChange={setDate} />
          <DatePicker label="With default date" defaultValue={new Date()} />
          <DatePicker label="Disabled" disabled defaultValue={new Date()} />
        </div>
      </DemoCard>

      <DemoCard title="Validation">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[var(--space-ui-medium-m)]">
          <DatePicker label="With error" error="Please pick a date" />
          <DatePicker label="With helper" helperText="Travel date" />
          <DatePicker label="Future only" minDate={new Date()} />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
